import { Business, Cancel, CheckCircle, Email, Person } from '@mui/icons-material';
import { Alert, Box, CircularProgress, Divider } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AdvisorInvitationVerificationResponse,
  RejectAdvisorInvitationRequest,
} from '../../models/Advisor';
import { publicAdvisorInvitationService } from '../../services/PublicAdvisorInvitationService';
import { createComprehensiveErrorHandler } from '../../utils/advisorErrorHandling';
import { StyledButton } from '../styles/StyledButtons';
import { StyledInput } from '../styles/StyledInputs';
import { StyledCard, StyledCardContent, StyledHeading, StyledTypography } from '../styles/styles';

interface RejectInvitationModalProps {
  isAdmin?: boolean;
}

export const RejectInvitationModal: React.FC<RejectInvitationModalProps> = () => {
  const { invitationId } = useParams<{ invitationId: string }>();
  const navigate = useNavigate();

  const [verificationData, setVerificationData] =
    useState<AdvisorInvitationVerificationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [rejectionReason, setRejectionReason] = useState('');

  // Verify invitation on component mount
  useEffect(() => {
    const verifyInvitation = async () => {
      if (!invitationId) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const verification =
          await publicAdvisorInvitationService.verifyAdvisorInvitation(invitationId);

        if (!verification.valid) {
          setError(verification.error || 'This invitation is no longer valid');
        } else {
          setVerificationData(verification);
        }
      } catch (error: any) {
        const errorHandler = createComprehensiveErrorHandler({
          operation: 'verify',
          invitationId,
        });
        const errorResult = errorHandler(error);
        setError(errorResult.message);
      } finally {
        setLoading(false);
      }
    };

    verifyInvitation();
  }, [invitationId]);

  const handleReject = async () => {
    if (!invitationId) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const rejectionData: RejectAdvisorInvitationRequest = {
        reason: rejectionReason.trim() || undefined,
      };

      const result = await publicAdvisorInvitationService.rejectAdvisorInvitation(
        invitationId,
        rejectionData,
      );

      if (result.success) {
        setSuccess(true);
        // Redirect to home after a short delay
        setTimeout(() => {
          navigate('/', {
            state: {
              message: 'Invitation declined. The inviter has been notified.',
            },
          });
        }, 3000);
      } else {
        setError(result.error || 'Failed to decline invitation');
      }
    } catch (error: any) {
      const errorHandler = createComprehensiveErrorHandler({
        operation: 'reject',
        invitationId,
      });
      const errorResult = errorHandler(error);
      setError(errorResult.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate(`/accept-invitation/${invitationId}`);
  };

  const getEntityTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      INDIVIDUAL: 'Individual',
      TRUST: 'Trust',
      COMPANY: 'Company',
      SMSF: 'SMSF',
      PARTNERSHIP: 'Partnership',
    };
    return labels[type] || type;
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      OBSERVER: 'Observer',
      DECISION_MAKER: 'Decision Maker',
    };
    return labels[role] || role;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <StyledTypography sx={{ mt: 2 }}>Verifying invitation...</StyledTypography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <StyledCard tone="tone1" sx={{ maxWidth: 500 }}>
          <StyledCardContent sx={{ py: 3, textAlign: 'center' }}>
            <Cancel sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
            <StyledHeading headerVariant="heading2" sx={{ mb: 2 }}>
              Invalid Invitation
            </StyledHeading>
            <StyledTypography sx={{ mb: 3 }}>{error}</StyledTypography>
            <StyledButton variant="contained" onClick={() => navigate('/')}>
              Go to Home
            </StyledButton>
          </StyledCardContent>
        </StyledCard>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <StyledCard tone="tone1" sx={{ maxWidth: 500 }}>
          <StyledCardContent sx={{ py: 3, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <StyledHeading headerVariant="heading2" sx={{ mb: 2 }}>
              Invitation Declined
            </StyledHeading>
            <StyledTypography sx={{ mb: 3 }}>
              The inviter has been notified of your decision. You will be redirected to the home
              page shortly.
            </StyledTypography>
            <CircularProgress size={24} />
          </StyledCardContent>
        </StyledCard>
      </Box>
    );
  }

  if (!verificationData?.invitation) {
    return null;
  }

  const invitation = verificationData.invitation;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2,
      }}
    >
      <StyledCard tone="tone1" sx={{ maxWidth: 600, width: '100%' }}>
        <StyledCardContent sx={{ py: 4 }}>
          <StyledHeading headerVariant="heading2" sx={{ mb: 3, textAlign: 'center' }}>
            Decline Invitation
          </StyledHeading>

          {/* Invitation Details */}
          <Box sx={{ mb: 4 }}>
            <StyledTypography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Invitation Details
            </StyledTypography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Person sx={{ mr: 1, color: 'text.secondary' }} />
              <StyledTypography>
                <strong>Invited by:</strong> {invitation.inviterName}
              </StyledTypography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Email sx={{ mr: 1, color: 'text.secondary' }} />
              <StyledTypography>
                <strong>Email:</strong> {invitation.inviterEmail}
              </StyledTypography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Business sx={{ mr: 1, color: 'text.secondary' }} />
              <StyledTypography>
                <strong>Role:</strong> {getRoleLabel(invitation.role)}
              </StyledTypography>
            </Box>

            {invitation.entities && invitation.entities.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <StyledTypography sx={{ mb: 1, fontWeight: 600 }}>
                  Investment Entities:
                </StyledTypography>
                {invitation.entities.map((entity) => (
                  <StyledTypography key={entity.id} variant="body2" sx={{ ml: 2 }}>
                    • {entity.name} ({getEntityTypeLabel(entity.type)})
                  </StyledTypography>
                ))}
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Rejection Form */}
          <Box sx={{ mb: 4 }}>
            <StyledTypography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Decline Invitation
            </StyledTypography>
            <StyledTypography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Are you sure you want to decline this invitation? You can optionally provide a reason
              for declining.
            </StyledTypography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <StyledInput
              label="Reason for declining (optional)"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              multiline
              rows={4}
              fullWidth
              placeholder="Please let us know why you're declining this invitation..."
              helperText="This reason will be shared with the person who sent the invitation"
            />
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <StyledButton variant="outlined" onClick={handleGoBack} disabled={submitting} fullWidth>
              Go Back
            </StyledButton>
            <StyledButton
              variant="contained"
              color="error"
              onClick={handleReject}
              disabled={submitting}
              fullWidth
            >
              {submitting ? <CircularProgress size={20} /> : 'Decline Invitation'}
            </StyledButton>
          </Box>
        </StyledCardContent>
      </StyledCard>
    </Box>
  );
};

export default RejectInvitationModal;
